import { buildAssigneesParam } from "shared/components/Assignee";
import {
  CreatedWithinOption,
  LeadsFilters,
  ShowingWithinOption
} from "./types";

const CREATED_WITHIN_DAYS: Partial<Record<CreatedWithinOption, number>> = {
  [CreatedWithinOption.Last7Days]: 7,
  [CreatedWithinOption.Last30Days]: 30
};

const SHOWING_WITHIN_DAYS: Partial<Record<ShowingWithinOption, number>> = {
  [ShowingWithinOption.Last7Days]: 7,
  [ShowingWithinOption.Last30Days]: 30
};

// "Next N days" presets have no matching BE preset param — the FE resolves
// them to an explicit showing_after/showing_before range client-side, the
// same way "Pick a date range" does. Unlike a custom range, this window must
// be recomputed from "now" on every request (not just once when the preset
// is selected) — otherwise a saved/URL-persisted filter goes stale the
// moment "now" moves past the range captured at selection time.
const SHOWING_UPCOMING_DAYS: Partial<Record<ShowingWithinOption, number>> = {
  [ShowingWithinOption.Next7Days]: 7,
  [ShowingWithinOption.Next30Days]: 30
};

const resolveShowingDateRange = (
  filters: LeadsFilters | undefined,
  upcomingDays: number | undefined
): { showingAfter?: string; showingBefore?: string } => {
  if (filters?.showingWithin === ShowingWithinOption.Custom)
    return {
      showingAfter: filters.showingAfter
        ? new Date(`${filters.showingAfter}T00:00:00`).toISOString()
        : undefined,
      showingBefore: filters.showingBefore
        ? new Date(`${filters.showingBefore}T23:59:59.999`).toISOString()
        : undefined
    };

  if (!upcomingDays) return {};

  const now = new Date();
  const upcoming = new Date();
  upcoming.setDate(upcoming.getDate() + upcomingDays);
  return {
    showingAfter: now.toISOString(),
    showingBefore: upcoming.toISOString()
  };
};

interface LeadsQueryArgs {
  page: number | string;
  perPage: number;
  propertyIds?: string[];
  unitIds?: string[];
  stageIds?: string[];
  sourceIds?: string[];
  assigneeIds: number[] | null;
  filters?: LeadsFilters;
  search?: string;
  starred?: boolean;
  contactId?: number;
  orderBy?: string;
  orderDirection?: "asc" | "desc";
}

// Single source of truth for the /portal/v1/leads query params, shared by the
// list view and each pipeline column so both stay in lockstep with the API.
export const buildLeadsSearchParams = (args: LeadsQueryArgs) => {
  const { filters } = args;

  const createdWithinDays = filters?.createdWithin
    ? CREATED_WITHIN_DAYS[filters.createdWithin]
    : undefined;
  const createdAfter =
    filters?.createdWithin === CreatedWithinOption.Custom &&
    filters.createdAfter
      ? new Date(`${filters.createdAfter}T00:00:00`).toISOString()
      : undefined;
  const createdBefore =
    filters?.createdWithin === CreatedWithinOption.Custom &&
    filters.createdBefore
      ? new Date(`${filters.createdBefore}T23:59:59.999`).toISOString()
      : undefined;

  const showingWithinDays = filters?.showingWithin
    ? SHOWING_WITHIN_DAYS[filters.showingWithin]
    : undefined;
  const showingUpcomingDays = filters?.showingWithin
    ? SHOWING_UPCOMING_DAYS[filters.showingWithin]
    : undefined;
  const { showingAfter, showingBefore } = resolveShowingDateRange(
    filters,
    showingUpcomingDays
  );

  return {
    page: args.page,
    per_page: args.perPage,
    property_ids: args.propertyIds,
    unit_ids: args.unitIds,
    stage_ids: args.stageIds?.length ? args.stageIds : undefined,
    source_ids: args.sourceIds?.length ? args.sourceIds : undefined,
    lead_types: filters?.leadTypes?.length ? filters.leadTypes : undefined,
    assignees: buildAssigneesParam(args.assigneeIds),
    most_recent_activity: filters?.activityTypes?.length
      ? { type: filters.activityTypes }
      : undefined,
    from_waitlist: filters?.onWaitlist || undefined,
    created_within_days: createdWithinDays,
    created_after: createdAfter,
    created_before: createdBefore,
    showing_within_days: showingWithinDays,
    showing_after: showingAfter,
    showing_before: showingBefore,
    starred: args.starred || undefined,
    search: args.search?.trim() || undefined,
    contact_id: args.contactId,
    order_by: args.orderBy,
    order_direction: args.orderDirection
  };
};
